import { ChangeDetectionStrategy, Component, Signal, computed, effect, inject, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CsIconModule } from '@components/icon';
import { QuizPhotoPairService } from '@services/quiz-photo-pair-service';
import { QuizPhotoSlot } from '@services/consultation-quiz-engine-service';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { UserService } from '@services/user';
import { QuizSecurePhotoNoteComponent } from '../quiz-secure-photo-note/quiz-secure-photo-note.component';

@Component({
  selector: 'app-quiz-photo-upload',
  templateUrl: './quiz-photo-upload.html',
  standalone: true,
  imports: [NgTemplateOutlet, CsIconModule, QuizSecurePhotoNoteComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** Photo card for a capture question. See client/components/quiz/CLAUDE.md. */
export class QuizPhotoUploadComponent {
  /** Resolved by the page, which decides the CTA layout from the same value. Absent means card mode. */
  readonly instructionImageUrl = input<string | undefined>(undefined);
  private readonly instantCheckupService = inject(InstantCheckupService);
  private readonly userService = inject(UserService);
  private readonly photoPair = inject(QuizPhotoPairService);
  readonly scanFrameImage: string = '/assets/images/complete-profile/scan-frame.svg';
  readonly dashedBorderImage: string = '/assets/images/quiz-photo-dashed-border.svg';
  readonly dashedBorderCardImage: string = '/assets/images/quiz-photo-dashed-border-card.svg';
  readonly checkIcon: string = 'circle_tick_filled';

  /** Tapping the dashed capture card runs the same instant-checkup action as the footer CTA. */
  readonly capture = output<void>();
  /** Tapping the already-uploaded preview opens that photo's instant-checkup detail (emits its id). */
  readonly openInstantCheckup = output<string>();

  readonly isTwoCard: Signal<boolean> = this.photoPair.showPair;
  readonly currentLabel: Signal<string> = this.photoPair.currentLabel;
  readonly alternateLabel: Signal<string> = this.photoPair.alternateLabel;
  readonly currentPhotoUrl: Signal<string> = computed(() => this.photoPair.currentPhoto()?.url ?? '');
  readonly alternatePhotoUrl: Signal<string> = computed(() => this.photoPair.alternatePhoto()?.url ?? '');

  private readonly instructionArtworkFailed = signal(false);

  readonly instructionSrc: Signal<string | undefined> = computed(() => {
    const url = this.instructionImageUrl();
    if (!url || !this.instructionArtworkFailed()) return url;
    return this.instantCheckupService.getPhotoInstructionFallbackUrl(url);
  });

  constructor() {
    effect(() => {
      this.instructionImageUrl();
      this.instructionArtworkFailed.set(false);
    });
  }

  onInstructionImageError(): void {
    this.instructionArtworkFailed.set(true);
  }

  readonly imageUrl: Signal<string> = computed(() => {
    const checkupType = this.photoPair.currentCheckupType();
    if (!checkupType) return '';
    return this.instantCheckupService.getPhotoIllustrationUrl(checkupType, this.userService.getUserGender());
  });

  onCapture(): void {
    const current = this.photoPair.currentPhoto();
    if (current) {
      void this.openCheckup(current);
      return;
    }
    this.capture.emit();
  }

  /** Keyboard activation for the div-as-button capture card (Enter/Space). */
  onCaptureKeydown(event: Event): void {
    event.preventDefault();
    this.onCapture();
  }

  async onOpenAlternate(): Promise<void> {
    const alternate = this.photoPair.alternatePhoto();
    if (alternate) await this.openCheckup(alternate);
  }

  private async openCheckup(photo: QuizPhotoSlot): Promise<void> {
    const checkupId = await this.photoPair.resolveCheckupId(photo);
    if (checkupId) this.openInstantCheckup.emit(checkupId);
  }

  onOpenAlternateKeydown(event: Event): void {
    event.preventDefault();
    void this.onOpenAlternate();
  }
}
